import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useScroll, useSpring } from "framer-motion";

import Footer from "@/components/layout/Footer";
import WhatsAppButton from "./components/layout/WhatsAppButton";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { PropertyErrorFallback } from "./components/properties/PropertyErrorFallback";
import CustomCursor from "./components/common/CustomCursor";
import SmartChatbot from "./components/chat/SmartChatbot";
import CookieBanner from "./components/common/CookieBanner";

import Home from "@/pages/Home";

// Lazy loaded routes
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Properties = lazy(() => import("@/pages/Properties"));
const PropertyDetails = lazy(() => import("@/pages/properties/[id]"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const LegalNotice = lazy(() => import("@/pages/LegalNotice"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Favorites = lazy(() => import("@/pages/Favorites"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-1 bg-[#a5694f] z-[100] origin-left"
      style={{ scaleX }}
    />
  );
}

const PageWrapper = ({ children }: { children: React.ReactNode }) => (
  <motion.div
    initial={{ opacity: 0, y: 15 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -15 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

function AnimatedRoutes() {
  const location = useLocation();
  const { t } = useTranslation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageWrapper><Home /></PageWrapper>} />
        <Route path="/about" element={<PageWrapper><About /></PageWrapper>} />
        <Route path="/contact" element={<PageWrapper><Contact /></PageWrapper>} />
        <Route path="/favorites" element={<PageWrapper><Favorites /></PageWrapper>} />
        <Route path="/privacy-policy" element={<PageWrapper><PrivacyPolicy /></PageWrapper>} />
        <Route path="/legal-notice" element={<PageWrapper><LegalNotice /></PageWrapper>} />
        <Route path="/terms-of-service" element={<PageWrapper><TermsOfService /></PageWrapper>} />

        <Route
          path="/properties"
          element={
            <ErrorBoundary fallback={<div className="py-40 text-center font-playfair italic text-stone-500">{t('errors.loading_properties', 'Error loading properties...')}</div>}>
              <PageWrapper><Properties /></PageWrapper>
            </ErrorBoundary>
          }
        />

        <Route
          path="/properties/:id"
          element={
            <ErrorBoundary fallback={<PropertyErrorFallback />}>
              <PageWrapper><PropertyDetails /></PageWrapper>
            </ErrorBoundary>
          }
        />
        <Route path="*" element={<PageWrapper><NotFound /></PageWrapper>} />
      </Routes>
    </AnimatePresence>
  );
}

export default function App() {
  const { i18n, t } = useTranslation();

  const seoData = {
    title: i18n.language === "es"
      ? "DEM Properties | Servicios Inmobiliarios y Selección de Viviendas"
      : "DEM Properties | Real Estate Services & Property Selection",
    description: i18n.language === "es"
      ? "Asesoramiento inmobiliario profesional. Encuentra tu próxima vivienda en nuestra selección de propiedades."
      : "Professional real estate advice. Find your next home in our property selection."
  };

  return (
    <HelmetProvider>
      <BrowserRouter>
        <Helmet>
          <title>{seoData.title}</title>
          <meta name="description" content={seoData.description} />
          <link rel="canonical" href="https://demproperties.es" />
          <meta property="og:title" content={seoData.title} />
          <meta property="og:description" content={seoData.description} />
        </Helmet>

        <ScrollToTop />
        <CustomCursor />
        <ScrollProgress />
        <WhatsAppButton />
        <SmartChatbot />
        <CookieBanner />

        <main className="min-h-screen">
          <Suspense fallback={<div className="fixed inset-0 bg-cream flex items-center justify-center z-[9999]"><div className="text-center"><h1 className="font-oswald text-3xl md:text-4xl uppercase tracking-[0.35em]" style={{ color: "#a5694f", textShadow: "0 0 14px rgba(207,166,112,0.45)" }}>DEM</h1><p className="font-playfair italic text-sm mt-3 text-charcoal/60">{t("home.loading", "Loading...")}</p></div></div>}>
            <AnimatedRoutes />
          </Suspense>
        </main>

        <Footer />
      </BrowserRouter>
    </HelmetProvider>
  );
}